import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './AllUsersPage.css';

const AllUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [inputType, setInputType] = useState<'phone' | 'userId'>('phone');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [userThreadData, setUserThreadData] = useState<any>(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    show: boolean;
    type?: 'success' | 'error';
  }>({
    message: '',
    show: false,
  });
  const [bucketMobile, setBucketMobile] = useState<string>('');
  const [bucketNo, setBucketNo] = useState<string>('');

  useEffect(() => {
    const userIdFromUrl = searchParams.get('userId');
    const phoneFromUrl = searchParams.get('phone');
    if (userIdFromUrl) {
      setSelectedUserId(userIdFromUrl);
      fetchUserThreadData(userIdFromUrl);
    }
    if (phoneFromUrl) {
      setPhoneNumber(phoneFromUrl);
    }
  }, [searchParams]);

  const fetchUserThreadData = async (userId: string) => {
    try {
      setThreadLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/messages-v3/user-quest-thread/${userId}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            ...(token && { 'x-auth-token': token }),
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUserThreadData(data);
      } else {
        setUserThreadData(null);
      }
    } catch (error) {
      console.error('Error fetching user thread data:', error);
      setUserThreadData(null);
    } finally {
      setThreadLoading(false);
    }
  };

  const fetchUserByPhone = async (phone: string) => {
    try {
      const phoneWithCountryCode = `91${phone}`;
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/signup/user-by-phone?phone=${phoneWithCountryCode}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            ...(token && { 'x-auth-token': token }),
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        return data.user_id;
      } else {
        const errorData = await response.json();
        if (errorData.detail === 'User not found') {
          throw new Error('No details found for this user');
        } else {
          throw new Error('Failed to fetch user details');
        }
      }
      return null;
    } catch (error) {
      console.error('Error fetching user by phone:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (inputType === 'phone' && phoneNumber.trim()) {
      try {
        const fetchedUserId = await fetchUserByPhone(phoneNumber.trim());
        if (fetchedUserId) {
          setSelectedUserId(fetchedUserId);
          fetchUserThreadData(fetchedUserId);
          navigate(
            `/all-users?userId=${fetchedUserId}&phone=${phoneNumber.trim()}`,
            {
              replace: true,
            }
          );
        } else {
          setToast({
            message: 'No user found for this phone number',
            show: true,
          });
          setSelectedUserId('');
          setUserThreadData(null);
          setTimeout(() => setToast({ message: '', show: false }), 3000);
        }
      } catch (error: any) {
        setToast({ message: error.message, show: true });
        setSelectedUserId('');
        setUserThreadData(null);
        setTimeout(() => setToast({ message: '', show: false }), 3000);
      }
    } else if (inputType === 'userId' && userId.trim()) {
      setSelectedUserId(userId.trim());
      fetchUserThreadData(userId.trim());
      navigate(`/all-users?userId=${userId.trim()}`, {
        replace: true,
      });
    }
  };

  const handleInputTypeChange = (type: 'phone' | 'userId') => {
    setInputType(type);
    setPhoneNumber('');
    setUserId('');
    setSelectedUserId('');
    setUserThreadData(null);
  };

  const handleCardClick = (threadId: string) => {
    navigate(`/thread/${threadId}/${selectedUserId}`);
  };

  const handleBucketSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bucketMobile.trim() || !bucketNo.trim()) {
      setToast({ message: 'Please fill all fields', show: true });
      setTimeout(() => setToast({ message: '', show: false }), 3000);
      return;
    }

    try {
      const phoneWithCountryCode = `91${bucketMobile.trim()}`;
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/profile/bucket/${bucketNo.trim()}?phone_number=${phoneWithCountryCode}`,
        {
          method: 'PUT',
          headers: {
            accept: 'application/json',
            ...(token && { 'x-auth-token': token }),
          },
        }
      );

      if (response.ok) {
        setToast({
          message: 'Bucket number updated successfully',
          show: true,
          type: 'success',
        });
        setBucketMobile('');
        setBucketNo('');
      } else {
        setToast({ message: 'Failed to update bucket number', show: true });
      }
    } catch (error) {
      setToast({ message: 'Error updating bucket number', show: true });
    } finally {
      setTimeout(() => setToast({ message: '', show: false }), 3000);
    }
  };

  return (
    <div className="all-users-page">
      <div className="content-header">
        <div className="header-content">
          <h1 className="page-title">User Quest Threads</h1>
          <p className="page-subtitle">
            Enter a phone number or user ID to view user's quest threads
          </p>
        </div>
      </div>

      <div className="users-container">
        <div className="user-input-section">
          <div className="input-type-toggle">
            <button
              type="button"
              className={`toggle-btn ${inputType === 'phone' ? 'active' : ''}`}
              onClick={() => handleInputTypeChange('phone')}
            >
              Phone Number
            </button>
            <button
              type="button"
              className={`toggle-btn ${inputType === 'userId' ? 'active' : ''}`}
              onClick={() => handleInputTypeChange('userId')}
            >
              User ID
            </button>
          </div>
          <form onSubmit={handleSubmit} className="user-id-form">
            {inputType === 'phone' ? (
              <input
                type="tel"
                value={phoneNumber}
                onChange={e => setPhoneNumber(e.target.value)}
                placeholder="Enter Phone Number (without +91)"
                className="user-id-input"
              />
            ) : (
              <input
                type="text"
                value={userId}
                onChange={e => setUserId(e.target.value)}
                placeholder="Enter User ID"
                className="user-id-input"
              />
            )}
            <button type="submit" className="search-button">
              Search
            </button>
          </form>
        </div>
        <div className="main-content-area">
          <div className="bucket-update-section">
            <h3>Bucket No Update</h3>
            <form onSubmit={handleBucketSubmit} className="bucket-form">
              <input
                type="tel"
                value={bucketMobile}
                onChange={e => setBucketMobile(e.target.value)}
                placeholder="Mobile Number (91 will be added automatically)"
                className="bucket-input"
              />
              <input
                type="text"
                value={bucketNo}
                onChange={e => setBucketNo(e.target.value)}
                placeholder="Bucket No"
                className="bucket-input"
              />
              <button type="submit" className="bucket-submit-btn">
                Submit
              </button>
            </form>
          </div>
          {selectedUserId ? (
            <div className="user-thread-display">
              <h3>User Quest Threads</h3>
              {threadLoading ? (
                <div className="thread-loading">
                  <div className="loading-spinner"></div>
                  <span>Loading user data...</span>
                </div>
              ) : userThreadData ? (
                <div className="thread-data">
                  <div className="thread-summary">
                    <p>
                      <strong>User ID:</strong> {userThreadData.user_id}
                    </p>
                    <p>
                      <strong>Total Records:</strong> {userThreadData.count}
                    </p>
                  </div>
                  <div className="records-grid">
                    {userThreadData.records.map((record: any) => (
                      <div
                        key={record.id}
                        className="record-card clickable"
                        onClick={() => handleCardClick(record.thread_id)}
                      >
                        <div className="record-header">
                          <span className={`entity-type ${record.entity_type}`}>
                            {record.entity_type.toUpperCase()}
                          </span>
                        </div>
                        <div className="record-content">
                          <p>
                            <strong>Thread ID:</strong> {record.thread_id}
                          </p>
                          <p>
                            <strong>Entity ID:</strong> {record.entity_id}
                          </p>
                          {record.title && (
                            <p>
                              <strong>Title:</strong> {record.title}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="no-data">
                  <p>No thread data available for this user</p>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
      {toast.show && (
        <div
          className={`toast ${toast.type === 'success' ? 'toast-success' : ''}`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default AllUsersPage;
